import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, BookOpen, Volume2 } from 'lucide-react';
import DocVideo from '../components/ui/DocVideo';
import { recordTermsAcceptance, needsTermsAcceptance } from '../components/FirstRunConsent';
import { useExtensionStore } from '../extensions/store';
import { openExternal } from '../utils/openExternal';
import { openHelp } from '../help/helpStore';
import { availableTools, hiddenForRole } from './rolePresets';
import { markOnboardingComplete, readOnboarding, useOnboardingStore } from './onboardingState';
import { buildSteps } from './steps/buildSteps';
import RolePicker from './steps/RolePicker';
import DoneFinale from './steps/DoneFinale';
import ToolList from './ToolList';

const EMPTY = [];
const TERMS_URL = 'https://apiboo.com/terms';
const PRIVACY_URL = 'https://apiboo.com/privacy';

/**
 * The first-run carousel (plan §1, approved preview). One card, one fixed
 * height on every step: media flush at the top, text below, dots, then
 * Back / Skip tour / Next. The Tools step is the full-height exception: its
 * text sits on top and the list fills the card.
 *
 * OWNER RULE: a pick or a toggle updates in place. Nothing here is keyed on
 * the role or the hidden set — only the step's own content is keyed on the
 * step id (for its short fade) — so the dialog never re-mounts, its open
 * animation never replays and a playing video keeps playing.
 *
 * Step 1 is the terms screen (FirstRunConsent's TERMS_VERSION and
 * recordTermsAcceptance, unchanged). Picks write `railHidden` straight into
 * the extensions store, so the real rail behind the card changes live.
 */
export default function OnboardingDialog({ startAt = 'welcome', onClose }) {
  const { t } = useTranslation();
  const tools = useMemo(() => availableTools(), []);
  const availableIds = useMemo(() => tools.map((tool) => tool.id), [tools]);
  const hidden = useExtensionStore((s) => s.railHidden) || EMPTY;
  const setRailHidden = useExtensionStore((s) => s.setRailHidden);
  const toggleRailHidden = useExtensionStore((s) => s.toggleRailHidden);
  const setRailHelpPulse = useOnboardingStore((s) => s.setRailHelpPulse);

  const [termsPending, setTermsPending] = useState(() => needsTermsAcceptance());
  const [roleId, setRoleId] = useState(() => readOnboarding()?.role || null);
  const [stepId, setStepId] = useState(() => (startAt === 'role' && !needsTermsAcceptance() ? 'role' : 'welcome'));

  const steps = useMemo(() => buildSteps(tools, hidden), [tools, hidden]);
  const index = Math.max(0, steps.findIndex((s) => s.id === stepId));
  const step = steps[index];
  const last = index === steps.length - 1;

  useEffect(() => {
    setRailHelpPulse(step.kind === 'done');
    return () => setRailHelpPulse(false);
  }, [step.kind, setRailHelpPulse]);

  const finish = useCallback(() => {
    markOnboardingComplete(roleId);
    onClose?.();
  }, [roleId, onClose]);

  const go = (i) => setStepId(steps[Math.max(0, Math.min(steps.length - 1, i))].id);

  const next = () => {
    if (step.kind === 'welcome' && termsPending) {
      recordTermsAcceptance();
      setTermsPending(false);
    }
    if (last) finish();
    else go(index + 1);
  };

  // Skip goes straight to the last step. (Until Plans v2 it stopped at "Three
  // ways to use Apiboo" first; that step sold the removed licence.)
  const skip = () => {
    markOnboardingComplete(roleId);
    setStepId('done');
  };

  const pickRole = (id) => {
    setRoleId(id);
    setRailHidden(hiddenForRole(id), availableIds);
  };

  // Escape: the terms cannot be dismissed; after them it is "skip and close".
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || termsPending) return;
      e.stopPropagation();
      finish();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [termsPending, finish]);

  const canNext = !(step.kind === 'role' && !roleId);
  const reachable = (i) => !termsPending && (i <= index || !!roleId);

  let media = null;
  let body = null;
  let title;
  let text;
  let extra = null;
  if (step.kind === 'welcome') {
    media = (
      <>
        <DocVideo id="welcome" narrated label={t('onboarding.welcome.title')} />
        <span className="ob-badge">
          <Volume2 size={13} aria-hidden="true" />
          {t('onboarding.narrated')}
        </span>
      </>
    );
    title = t('onboarding.welcome.title');
    text = t('onboarding.welcome.text');
    if (termsPending) {
      extra = (
        <p className="ob-legal">
          {t('onboarding.welcome.legalBefore')}{' '}
          <button type="button" className="ob-link" onClick={() => openExternal(TERMS_URL)}>
            {t('onboarding.welcome.terms')}
          </button>{' '}
          {t('onboarding.welcome.legalAnd')}{' '}
          <button type="button" className="ob-link" onClick={() => openExternal(PRIVACY_URL)}>
            {t('onboarding.welcome.privacy')}
          </button>
          {t('onboarding.welcome.legalAfter')}
        </p>
      );
    }
  } else if (step.kind === 'role') {
    media = <RolePicker roleId={roleId} tools={tools} onPick={pickRole} />;
    title = t('onboarding.role.title');
    text = t('onboarding.role.text');
  } else if (step.kind === 'tools') {
    body = (
      <div className="panel-scroll ob-card-scroll">
        <ToolList roleId={roleId} tools={tools} hidden={hidden} onToggle={(id) => toggleRailHidden(id, availableIds)} />
      </div>
    );
    title = t('onboarding.tools.title');
    text = t('onboarding.tools.text');
  } else if (step.kind === 'feature') {
    const { tool } = step;
    media = (
      <>
        <DocVideo id={tool.video} label={t(`onboarding.feature.${tool.key}.title`)} />
        <span className="ob-badge">
          <tool.Icon size={13} aria-hidden="true" />
          {t(`onboarding.tool.${tool.key}.name`)}
        </span>
      </>
    );
    title = t(`onboarding.feature.${tool.key}.title`);
    text = t(`onboarding.feature.${tool.key}.text`);
  } else {
    const hiddenSet = new Set(hidden);
    media = <DoneFinale visibleTools={tools.filter((tool) => !hiddenSet.has(tool.id))} />;
    title = t('onboarding.done.title');
    text = t('onboarding.done.text');
  }

  const textBlock = (
    <div key="text" className="ob-card-text">
      <div key={step.id} className="ob-fade">
        <h2 id="ob-title">{title}</h2>
        <p>{text}</p>
        {extra}
      </div>
    </div>
  );

  const primaryLabel =
    step.kind === 'welcome' && termsPending ? t('onboarding.agree') : last ? t('onboarding.start') : t('onboarding.next');

  return (
    <div className="ob-overlay">
      <div className="modal-backdrop" aria-hidden="true" />
      <div
        className={`modal-content ob-card${body ? ' ob-card--full' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ob-title"
        data-testid="onboarding-dialog"
      >
        {body
          ? [
              textBlock,
              <div key="body" className="ob-card-body">
                {body}
              </div>,
            ]
          : [
              <div key="media" className="ob-card-media">
                <div key={step.id} className="ob-fade ob-card-media-inner">
                  {media}
                </div>
              </div>,
              textBlock,
            ]}

        <div className="ob-dots" role="tablist" aria-label={t('onboarding.stepsLabel')}>
          {steps.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={t('onboarding.stepLabel', { n: i + 1, total: steps.length })}
              className={`ob-dot${i === index ? ' ob-dot--on' : i < index ? ' ob-dot--done' : ''}`}
              disabled={!reachable(i)}
              onClick={() => go(i)}
            />
          ))}
        </div>

        <div className="ob-foot">
          {index > 0 ? (
            <button type="button" className="ob-btn" onClick={() => go(index - 1)}>
              <ArrowLeft size={15} aria-hidden="true" />
              {t('onboarding.back')}
            </button>
          ) : (
            <span className="ob-count">{t('onboarding.takesAMinute')}</span>
          )}
          <span className="ob-foot-sp" />
          {index > 0 && !last && (
            <button type="button" className="ob-skip" onClick={skip}>
              {t('onboarding.skip')}
            </button>
          )}
          {last && (
            <button
              type="button"
              className="ob-btn"
              onClick={() => {
                finish();
                openHelp();
              }}
            >
              <BookOpen size={15} aria-hidden="true" />
              {t('onboarding.openHelp')}
            </button>
          )}
          <button type="button" className="ob-btn ob-btn--primary" onClick={next} disabled={!canNext} autoFocus>
            {primaryLabel}
            {!last && <ArrowRight size={15} aria-hidden="true" />}
          </button>
        </div>
      </div>
    </div>
  );
}
